import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useLanguage } from './LanguageContext';

const QuizContext = createContext();

// Quiz reducer
const quizReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_QUIZ_START':
      return { 
        ...state, 
        isLoading: true, 
        error: null 
      };
    case 'LOAD_QUIZ_SUCCESS':
      return {
        ...state,
        isLoading: false,
        currentQuiz: action.payload,
        questions: action.payload.questions,
        totalQuestions: action.payload.totalQuestions,
        currentQuestionIndex: 0,
        answers: {},
        score: null,
        showResults: false,
        error: null
      };
    case 'LOAD_QUIZ_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'ANSWER_QUESTION':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: action.payload.answer
        }
      };
    case 'NEXT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: state.currentQuestionIndex + 1
      };
    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1)
      };
    case 'SUBMIT_QUIZ':
      const score = calculateScore(state.questions, state.answers);
      const result = {
        userId: action.payload.userId,
        userName: action.payload.userName,
        subject: state.currentQuiz.subject,
        subjectId: state.currentQuiz.subjectId,
        score: score.score,
        totalQuestions: score.totalQuestions,
        percentage: score.percentage,
        answers: state.answers,
        completedAt: new Date().toISOString(),
        timeSpent: action.payload.timeSpent
      };
      
      // Save result to localStorage
      saveQuizResult(result);
      
      return {
        ...state,
        score: score,
        showResults: true,
        currentResult: result
      };
    case 'RESET_QUIZ':
      return {
        ...state,
        currentQuestionIndex: 0,
        answers: {},
        score: null,
        showResults: false,
        currentResult: null
      };
    case 'GO_TO_QUESTION':
      return {
        ...state,
        currentQuestionIndex: action.payload
      };
    default:
      return state;
  }
};

// Calculate quiz score
const calculateScore = (questions, answers) => {
  let correct = 0;
  const totalQuestions = questions.length;
  
  questions.forEach((question, index) => {
    if (answers[question.id] === question.answer) {
      correct++;
    }
  });
  
  return {
    score: correct,
    totalQuestions,
    percentage: Math.round((correct / totalQuestions) * 100)
  };
};

// Save quiz result to localStorage
const saveQuizResult = (result) => {
  try {
    const existingResults = JSON.parse(localStorage.getItem('quizResults')) || [];
    existingResults.push(result);
    localStorage.setItem('quizResults', JSON.stringify(existingResults));
  } catch (error) {
    console.error('Error saving quiz result:', error);
  }
};

// Initial state
const initialState = {
  isLoading: false,
  currentQuiz: null,
  questions: [],
  totalQuestions: 0,
  currentQuestionIndex: 0,
  answers: {},
  score: null,
  showResults: false,
  currentResult: null,
  error: null
};

// Quiz Provider
export const QuizProvider = ({ children }) => {
  const [state, dispatch] = useReducer(quizReducer, initialState);
  const { language } = useLanguage();

  // Load quiz data
  const loadQuiz = async (subjectId) => {
    dispatch({ type: 'LOAD_QUIZ_START' });
    
    try {
      const response = await fetch(`/data/batch44/${subjectId}.json`);
      if (!response.ok) {
        throw new Error(`Failed to load quiz for ${subjectId}`);
      }
      
      const quizData = await response.json();
      dispatch({ type: 'LOAD_QUIZ_SUCCESS', payload: quizData });
    } catch (error) {
      dispatch({ type: 'LOAD_QUIZ_ERROR', payload: error.message });
    }
  };

  // Answer a question
  const answerQuestion = (questionId, answer) => {
    dispatch({ 
      type: 'ANSWER_QUESTION', 
      payload: { questionId, answer } 
    });
  };

  // Navigate to next question
  const nextQuestion = () => {
    dispatch({ type: 'NEXT_QUESTION' });
  };

  // Navigate to previous question
  const previousQuestion = () => {
    dispatch({ type: 'PREVIOUS_QUESTION' });
  };

  // Go to specific question
  const goToQuestion = (index) => {
    dispatch({ type: 'GO_TO_QUESTION', payload: index });
  };

  // Submit quiz
  const submitQuiz = (userId, userName, timeSpent) => {
    dispatch({ 
      type: 'SUBMIT_QUIZ', 
      payload: { userId, userName, timeSpent } 
    });
  };

  // Reset quiz
  const resetQuiz = () => {
    dispatch({ type: 'RESET_QUIZ' });
  };

  // Get quiz results from localStorage
  const getQuizResults = () => {
    try {
      return JSON.parse(localStorage.getItem('quizResults')) || [];
    } catch (error) {
      console.error('Error loading quiz results:', error);
      return [];
    }
  };

  // Get current question
  const getCurrentQuestion = () => {
    if (!state.questions.length) return null;
    return state.questions[state.currentQuestionIndex];
  };

  // Get question display text based on language
  const getQuestionText = (question) => {
    if (!question) return '';
    return language === 'ar' ? question.questionAr : question.question;
  };

  // Get options display text based on language
  const getOptionsText = (question) => {
    if (!question) return [];
    return language === 'ar' ? question.optionsAr : question.options;
  };

  // Get explanation text based on language
  const getExplanationText = (question) => {
    if (!question) return '';
    return language === 'ar' ? question.explanationAr : question.explanation;
  };

  // Check if quiz is completed
  const isQuizCompleted = () => {
    return state.currentQuestionIndex >= state.questions.length - 1;
  };

  // Get answered questions count
  const getAnsweredCount = () => {
    return Object.keys(state.answers).length;
  };

  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return false;
    return state.answers.hasOwnProperty(currentQuestion.id);
  };

  const value = {
    ...state,
    loadQuiz,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    submitQuiz,
    resetQuiz,
    getQuizResults,
    getCurrentQuestion,
    getQuestionText,
    getOptionsText,
    getExplanationText,
    isQuizCompleted,
    getAnsweredCount,
    isCurrentQuestionAnswered
  };

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
};

// Custom hook
export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};

export default QuizContext;
